import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import { Card } from '../../components/UI';
import { HiOutlineChatAlt, HiOutlineCog, HiOutlineTable, HiOutlineViewList, HiOutlineLink } from 'react-icons/hi';
import WhatsAppTemplates from './WhatsAppTemplates';
import GoogleSheetIntegration from './GoogleSheetIntegration';
import PipelineStages from './PipelineStages';
import Integrations from './Integrations';

const tabs = [
  { id: 'whatsapp-templates', label: 'WhatsApp Templates', icon: HiOutlineChatAlt },
  { id: 'google-sheet', label: 'Google Sheets', icon: HiOutlineTable },
  { id: 'pipeline-stages', label: 'Pipeline Stages', icon: HiOutlineViewList },
  { id: 'integrations', label: 'Integrations', icon: HiOutlineLink },
];

const Settings = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get active tab from URL
  const getActiveTab = () => {
    const path = location.pathname.split('/').pop();
    if (path && path !== 'settings') {
      return path;
    }
    return 'whatsapp-templates';
  };

  const [activeTab, setActiveTab] = useState(getActiveTab());

  // Update active tab when URL changes
  useEffect(() => {
    const newTab = getActiveTab();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.pathname]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/app/settings/${tabId}`, { replace: true });
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'whatsapp-templates':
        return <WhatsAppTemplates />;
      case 'google-sheet':
        return <GoogleSheetIntegration />;
      case 'pipeline-stages':
        return <PipelineStages />;
      case 'integrations':
        return <Integrations />;
      default:
        return <WhatsAppTemplates />;
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
            <HiOutlineCog className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-500 text-xs sm:text-sm">Manage your application settings</p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="border-b border-gray-200 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
          <nav className="flex gap-2 sm:gap-4 min-w-max" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                    ${isActive
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.id === 'whatsapp-templates' ? 'WhatsApp' : 'Sheets'}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-4 sm:mt-6">
          {renderContent()}
        </div>
      </div>
    </Layout>
  );
};

export default Settings;